import {reactive} from 'vue';
import {getCollection} from '../../lib/filters/filters';
import {
  addSubcollectionDocument,
  currentUser,
  deleteSubcollectionDocument,
  updateDocument,
  updateSubcollectionDocument,
} from '../FirestoreService';
import {Feature} from './Feature';
import {MapService} from '../MapService';
import {Timestamp} from 'firebase/firestore';
import {OverlayService} from './OverlaysService';
import {getStore} from '@/services/store';
import {InfoWindowContent} from '@/lib/classes/InfoWindowContent';
import {FeatureManager} from '@/lib/classes/FeatureManager/FeatureManager';
import {FEATURES_COLLECTION, OVERLAY_COLLECTION, TAX_FEATURES_COLLECTION} from '@/lib/constants';
import {compressObjectToBase64} from '@northpoint-development/labs-common-core';

/** @typedef {import('./Feature.js').FeatureData} FeatureData */

/**
 * Private helper to check if a Feature should be rendered.
 * @param {string} featureId The ID of the Feature to check.
 * @param {boolean} isTaxFeature
 * @return {boolean}
 * @private
 */
function shouldRenderFeature(featureId, isTaxFeature) {
  return getCollection(isTaxFeature ? 'taxFeatures' : 'features').getFilteredIds().has(featureId);
}

/**
 * Private helper to to handle dates in either Firestore or JS format.
 * @param {Date|Timestamp} date
 * @return {Date}
 */
function _dateFromData(date) {
  if (!date) {
    return new Date();
  }
  if (date instanceof Timestamp) {
    return date.toDate();
  }
  if (date instanceof Date) {
    return date;
  }
  throw new Error('Invalid date format: ' + JSON.stringify(date));
}
/**
 * Flattens out a folder array to return a one dimensional array of
 * all features contained within the folder structure
 * @param {Array} arr - The array of nested folders to flatten
 * @param {number[]} path - The array to flatten
 * @return {Map<string, number[]>} A map whose keys are feature IDs and values are the
 * path to that feature within the folders structure
 */
function _flattenFolderFeatures(arr) {
  const output = new Map();
  function flatten(arr, path = []) {
    return arr.reduce((acc, curr, index) => {
      if (curr.id) {
        acc.push({id: curr.id, path: path.concat(index)});
      }
      if (curr.children) {
        acc = acc.concat(flatten(curr.children, path.concat(index)));
      }
      return acc;
    }, []);
  }
  flatten(arr).map((i) => output.set(i.id, i.path));

  return output;
}

/**
 * An Overlay represents a collection of Features.
 */
class Overlay extends FeatureManager {
  /**
   * Creates a new Overlay object, and initializes all the given features.
   * @param {OverlayData} data Data from the database to create the overlay.
   */
  constructor(data) {
    super(data);
    /** @type {boolean} Indicates whether the overlay is private only to the user who created it */
    this.private = !!data.private;
    /** @type {boolean} Indicates whether or not this overlay should ever be seen */
    this.visible = data.defaultVisibility || false;
    /** @type {Map<string, Feature>} */
    this.features = new Map();

    // #region Unique Overlay properties
    /** @type {import('@/lib/classes/FeatureManager/types').ParentData} The parent project of this overlay */
    this.parent = data.parent || null;
    /** @type {Date} The timestamp when this overlay was created */
    this.createdAt = _dateFromData(data.createdAt);
    /** @type {Date} The timestamp when this overlay was last edited */
    this.editedAt = _dateFromData(data.editedAt);
    /** @type {string} The ID of the user who created this overlay */
    this.createdByUid = data.createdByUid || 'unknown'; // TODO remove?
    /** @type {string} The ID of the user who last edited this overlay */
    this.editedByUid = data.editedByUid || 'unknown'; // TODO remove?
    /** @type {string} The display name of the user who created this overlay */
    this.createdBy = data.createdBy || 'Unknown User';
    /** @type {string} The display name of the user who last edited this overlay */
    this.editedBy = data.editedBy || 'Unknown User';
    /** @type {boolean} Indicates whether or not this overlay is a tax overlay */
    this.isTaxOverlay = data.isTaxOverlay || false;
    /** @type {string} As of date is a string so that the input box is accurate */
    this.asOfDate = data.asOfDate ? _dateFromData(data.asOfDate) : null;
    /** @type {Array<string>} */
    this.tags = data.tags ?? [];
    // #endregion

    /** @type {string} */
    this.collectionName = OVERLAY_COLLECTION;
    /** @type {string} The local filter collection name (e.g. 'features', 'taxFeatures) */
    this.featureCollectionName = this.isTaxOverlay ? TAX_FEATURES_COLLECTION : FEATURES_COLLECTION;

    const folderFeatures = _flattenFolderFeatures(this.folders);
    if (data.features instanceof Map) {
      const featuresCollection = this.isTaxOverlay ? getCollection('taxFeatures') : getCollection('features');
      // the features may have already been added to the features collection by geohash, so we need to check. Build a
      // list and add to the collection once to minimize subscriber updates.
      const toAdd = [];
      const canEditFeature = (!this.isTaxOverlay) || (getStore().state.taxTeamIds.includes(currentUser().uid));
      for (const [fid, f] of data.features) {
        if (featuresCollection.sourceHasId(fid)) {
          /** @type {Feature} */
          const existingFeature = featuresCollection.getItem(fid);
          if (existingFeature) {
            this.addOverlayInfoWindowListener(existingFeature);
            this.features.set(existingFeature.id, existingFeature);
          }
        } else {
          try {
            const feature = new Feature(f, this);
            if (canEditFeature) {
              feature.addEditListeners();
            } else {
              feature.addViewListeners();
            }
            feature.addHighlightListeners();
            this.addOverlayInfoWindowListener(feature);
            this.features.set(feature.id, feature);
            toAdd.push(feature);
          } catch (e) {
            console.error(`Error creating feature for overlay ${this.name} (${this.id})`, e);
          }
        }
        // Push the feature to the root of folders if it doesn't exist
        if (!folderFeatures.has(f.id)) {
          this.folders.push({id: f.id});
        }
      }
      if (toAdd.length > 0) {
        featuresCollection.addItems(...toAdd);
      }
    }

    this.updateFeatureRenderOrder();

    // Making this object reactive enables watchers to work correctly
    const reactiveThis = reactive(this);
    return reactiveThis;
  }

  /**
   * Applies updated data to this overlay and persists it to the database. To only force an update of the editor and
   * timestamp, pass `{}` as the data. While this function is executing, the `editedAt` field will be `null`.
   * @param {OverlayData} data The updated data to apply to this overlay.
   * @param {boolean} updateEditedBy true when we want to update who it was edited by, false
   * @return {Promise<Overlay>} A promise that resolves to this overlay.
   */
  async update(data, updateEditedBy = true) {
    // The intention is to target Toolbox Overlays - we don't need to save them.
    // private is currently only associated with Toolbox Overlays. This may need to be updated in the future.
    if (this.private) return;

    // as soon as the update begins, indicate that the current data has not yet been saved by setting the editedAt
    // value to null.
    const originalEditedAt = this.editedAt;
    this.editedAt = null;

    // anything other than an object (including when no args are passed at all) is treated as an empty object
    if (!data || typeof data !== 'object') {
      data = {};
    }

    // Only valid fields for an Overlay should be set in the database, so filter out anything that isn't a property
    // of `this`.
    const applyChanges = {};
    Object.entries(data).forEach(([key, value]) => {
      if (Object.hasOwn(this, key)) {
        applyChanges[key] = value;
      }
    });
    // some properties are not stored in the database, so remove them before updating
    delete applyChanges.features;
    delete applyChanges.id;
    delete applyChanges.editMode;

    if (updateEditedBy) {
      // any time the document is updated, even if there are no changes, we need to update the editor and timestamp
      applyChanges.editedByUid = currentUser().uid;
      applyChanges.editedBy = currentUser().displayName;
      applyChanges.editedAt = new Date();
    } else {
      applyChanges.editedByUid = this.editedByUid;
      applyChanges.editedBy = this.editedBy;
      applyChanges.editedAt = originalEditedAt;
    }

    // Do not apply changes to this until the database call has succeeded. Consumers may monitor this.editedAt to
    // determine if saving is in progress.
    await updateDocument(OVERLAY_COLLECTION, this.id, {
      ...applyChanges,
      // Not ideal to always update folders, but we would otherwise need to compress, save, and then decompress before
      // applying the changes to 'this'.
      folders: await compressObjectToBase64(applyChanges.folders || this.folders),
    });

    Object.assign(this, applyChanges);
    this.updateFeatureRenderOrder();
    return this;
  }

  /**
   * Adds a listener to a feature to open its parent overlay's info window when clicked.
   * @param {Feature} feature The feature to add a listener to.
   */
  async addOverlayInfoWindowListener(feature) {
    try {
      await feature.addHandler('click', 'overlayInfoWindow', (event) => {
        const selectedOverlayId = getStore().state.selectedOverlay?.id;
        if (this.editMode || (selectedOverlayId === this.id)) return;

        event = event.domEvent ?? event;
        const location = MapService.screenPixelToLatLng(event.pageX, event.pageY);

        this.openInfoWindow(feature, location);
      });
    } catch (error) {
      error.message = `Error adding overlay info window listener to feature (${feature.name}) (${feature.id}): ${error.message}`;
      console.error(error);
    }
  }

  /**
   * Opens an Info Window for this overlay at the given location.
   * @param {Feature} feature The feature that triggered the info window to open.
   * @param {google.maps.LatLng} location The location to open the info window at.
   */
  openInfoWindow(feature, location) {
    try {
      const hasParentProject = this.parent?.id ?? false;

      /**
       * Helper function that creates a promise for fetching either tax overlays or normal overlays, depending
       * On the type of overlay for the clicked feature
       * @return {Promise<Overlay[] | false>} A promise that resolves to the overlays to display in the info window.
       */
      const fetchOverlays = async () => {
        let overlays;
        if (hasParentProject) {
          const parentOverlays = await OverlayService.overlaysByParent(this.parent.id);
          // filter parent overlays to only include other tax overlays or other normal overlays
          overlays = parentOverlays.filter((overlay) => overlay.isTaxOverlay === this.isTaxOverlay);
        } else {
          overlays = await OverlayService.overlayById(this.id);
        }
        return overlays;
      };

      const content = new InfoWindowContent({
        type: this.isTaxOverlay ? 'taxOverlay' : 'overlay',
        title: this.name,
        location: location,
        id: this.id,
        metadata: {
          projectId: this.parent?.id,
        },
        overlays: fetchOverlays(),
      });

      getStore().commit('infowindow/updateInfoWindowContents', {content});
    } catch (error) {
      error.message = `Error opening info window for Overlay ${this.name} (${this.id}): ${error.message}`;
      console.error(error);
    }
  }

  /**
   * Sets the render status of all the features of this overlay. If the overlay is not visible, this will
   * do nothing. Otherwise, the render status will be set to the value of the `render` parameter.
   * @param {boolean} render Indicates whether all the features of this overlay should be rendered or not.
   */
  setRender(render) {
    if (this.render === render) return;
    this.render = render;
    if (!this.visible) return;
    for (const feature of this.features.values()) {
      feature.setRender(render && shouldRenderFeature(feature.id, this.isTaxOverlay));
    }
  }

  /**
   * setVisibility sets the visibility of this overlay for the current browser session. This will not persist the
   * visibility setting to the database.
   * @param {boolean} visible true to make this overlay visible, false to make it invisible
   */
  setVisibility(visible) {
    if (this.visible === visible) return;
    this.visible = visible;
    if (!this.render) return;
    for (const feature of this.features.values()) {
      feature.setRender(visible && shouldRenderFeature(feature.id, this.isTaxOverlay));
    }
  }

  /**
   * newFeature creates a new feature for this overlay and saves it to the database.
   * @param {Object} data The feature to add to this overlay.
   * @param {string} featureId - An optional document ID that a feature should use
   * @param {number[]} folderPath - Whether or not to restore the feature to its folder
   * @param {boolean} [fromRestoredFolder] - Special flag if being called from the folder restore function.
   * This is used to prevent pushing a "new feature" from the restore function to the root of folders unnecessarily.
   * @return {Promise<Feature>} A promise that resolves to the newly created feature.
   */
  async newFeature(data, featureId = null, folderPath = null, fromRestoredFolder = false) {
    // make a new variable validData for if we are undoing deletion of a feature with infoWindowContent
    const validData = data;
    if (!data) {
      console.error(`Overlay.newFeature called without data for overlay ${this.name}`);
      return;
    }
    // If the data has infoWindowContent (basically whenever we undo delete),
    // Get rid of it before we undo deleting the feature
    if (data?.infoWindowContent) {
      delete validData.infoWindowContent;
    }
    // Convert custom displayField objects to normal objects for saving to Firestore
    if (data?.displayFields?.length) {
      validData.displayFields = data.displayFields.map((displayField) => JSON.parse(JSON.stringify(displayField)));
    }

    // Set the flag for whether this feature is a tax feature
    validData.isTaxFeature = this.isTaxOverlay;

    // For a new feature, we must create the Firestore doc to get the featureId before attempting to create the Feature.
    // In that case we'll have to undo the creation of the Firestore doc if creating the Feature fails.
    const isNew = !featureId;
    if (isNew) {
      try {
        featureId = await addSubcollectionDocument(OVERLAY_COLLECTION, this.id, FEATURES_COLLECTION, validData);
      } catch (error) {
        error.message = `Overlay.newFeature failed to create new feature in overlay ${this.name}: ${error.message}`;
        console.error(error);
        return;
      }
    }
    let feature;
    try {
      feature = new Feature({...validData, id: featureId}, this);
      feature.addEditListeners();
      feature.addHighlightListeners();
      this.addOverlayInfoWindowListener(feature);
    } catch (err) {
      console.error(`Overlay.newFeature failed to add new feature to overlay ${this.name}: ${err}`);
      // Undo the creation of the feature in Firestore.
      if (isNew) {
        await deleteSubcollectionDocument(OVERLAY_COLLECTION, this.id, FEATURES_COLLECTION, featureId);
      }
      return;
    }
    if (!isNew) {
      await updateSubcollectionDocument(OVERLAY_COLLECTION, this.id, FEATURES_COLLECTION, featureId, validData);
    }
    if (folderPath) {
      const targetFolder = this.getFolderItem(folderPath);
      const folderToRestore = targetFolder?.parentFolder ? targetFolder.parentFolder : this.folders;
      this.addFolderItem(folderToRestore, {id: feature.id}, folderPath[folderPath.length - 1]);
    } else if (fromRestoredFolder === false) {
      this.folders.unshift({id: feature.id});
    }
    // Toolbox features are handled separately. This logic is here instead of in Toolbox.vue because when undoing
    // deletion, the restore function doesn't have access to anything in Toolbox.vue. If the toolBoxMinZoom is not
    // null, the feature is a toolbox feature
    // TODO: come up with a better architecture for this
    if (feature.toolBoxMinZoom !== null) {
      // the toolbox feature is permanently in "edit mode", so if there are any features, make sure this overlay is in
      // edit mode
      this.editMode = true;
      feature.addHandler('click', 'toolboxSetEditing', async (event) => await feature.setEditing(true, {event}));
    }
    this.features.set(feature.id, feature);
    const collectionName = this.isTaxOverlay ? 'taxFeatures' : 'features';
    getCollection(collectionName).addItems(feature);
    await this.update({folders: this.folders}); // force editor and timestamp to be updated
    return feature;
  }

  /**
   * addFeature adds an existing feature to this overlay object for tracking and control. This does NOT create a new
   * feature or modify this overlay.
   * @param {Object} data The feature data to add to this overlay.
   * @return {Feature} A promise that resolves to the newly created feature.
   */
  addFeature(data) {
    let feature;
    try {
      feature = new Feature(data, this);

      const canEditFeature = (!this.isTaxOverlay) || (getStore().state.taxTeamIds.includes(currentUser().uid));
      if (canEditFeature) {
        feature.addEditListeners();
      } else {
        feature.addViewListeners();
      }

      feature.addHighlightListeners();
      this.addOverlayInfoWindowListener(feature);
    } catch (err) {
      console.error(`Overlay.addFeature failed to add feature to overlay "${this.name}": ${err}`);
      return; // Can't proceed with adding the feature
    }

    this.features.set(feature.id, feature);

    const folderFeatures = _flattenFolderFeatures(this.folders);
    // Push the feature to the root of folders if it doesn't exist
    if (!folderFeatures.has(feature.id)) {
      this.folders.push({id: feature.id});
    }
    return feature;
  }
}

export {
  Overlay,
};
